import { Controller } from '@hotwired/stimulus';

// Periodically checks analysis status and only refreshes when analysis is terminal.
// This avoids hijacking navigation while still auto-transitioning to the next step.
//
// Usage:
// data-controller="polling-refresh"
// data-polling-refresh-interval-value="7000"
// data-polling-refresh-status-url-value="/onboarding/:id/analysis_status"
// data-polling-refresh-reload-url-value="/onboarding/:id/edit"
export default class extends Controller {
  static values = {
    interval: { type: Number, default: 7000 },
    statusUrl: String,
    reloadUrl: String,
  };

  connect() {
    this.isNavigatingAway = false;
    this.isPolling = false;
    this.abortController = null;
    this.beforeVisitHandler = () => {
      this.isNavigatingAway = true;
      this.stopPolling();
    };

    document.addEventListener('turbo:before-visit', this.beforeVisitHandler);

    this.timer = setInterval(() => {
      this.poll();
    }, this.intervalValue);
  }

  disconnect() {
    document.removeEventListener('turbo:before-visit', this.beforeVisitHandler);
    this.stopPolling();
  }

  async poll() {
    if (this.isNavigatingAway || this.isPolling) return;
    this.isPolling = true;

    try {
      if (!this.hasStatusUrlValue) {
        // Backward-compatible fallback for any legacy usage.
        window.Turbo.visit(window.location.href, { action: 'replace' });
        return;
      }

      this.abortController = new AbortController();

      const response = await fetch(this.statusUrlValue, {
        headers: { Accept: 'application/json' },
        signal: this.abortController.signal,
      });

      if (!response.ok) return;

      const data = await response.json();
      if (!data.terminal || this.isNavigatingAway) return;

      this.stopPolling();
      window.Turbo.visit(data.redirect_url || this.reloadUrlValue || window.location.href, {
        action: 'replace',
      });
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('polling-refresh request failed', error);
      }
    } finally {
      this.isPolling = false;
      this.abortController = null;
    }
  }

  stopPolling() {
    clearInterval(this.timer);
    if (this.abortController) this.abortController.abort();
  }
}
